import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "app/store";
import { Document } from "models/schemas";
import {
  DeleteIngestedDocumentsResponse,
  IngestDocumentsRequest,
} from "pages/gaia-ai/definitions";
import {
  useIngestDocumentsMutation,
  useDeleteIngestedDocumentsMutation,
} from "pages/gaia-ai/endpoint";
import { useSubmitKBApprovalMutation } from "pages/gaia-ai/kb-approval-endpoints";
import {
  useUploadUrlMutation,
  useLazyDownloadUrlQuery,
} from "components/documents-table-os/endpoints";
import { UploadUrlRequest } from "components/documents-table-os/definitions";
import { useSelectedTeam } from "features/team-settings/hooks";
import { sleep } from "utils/document-utils";
import {
  uploadFileWithProgress,
  UploadProgress,
  CancelToken,
} from "utils/upload-progress-utils";
import {
  extractUniqueKey,
  getDocumentUrlFromPresigned,
  buildS3Key,
} from "utils/document-utils";
import {
  getFileNameWithoutExtension,
  getFileExtension,
} from "utils/date-format";
import { getFileSize } from "utils/date-format";

const ATLAS_BATCH_SIZE = 50;

interface UseAtlasFileOperationsProps {
  teamName: string;
  addNotification: (notification: {
    type: "success" | "error" | "info" | "warning";
    header: string;
    dismissible: boolean;
    timeout: number;
  }) => void;
}

export const useAtlasFileOperations = ({
  teamName,
  addNotification,
}: UseAtlasFileOperationsProps) => {
  const [ingestDocuments] = useIngestDocumentsMutation();
  const [deleteIngestedDocuments] = useDeleteIngestedDocumentsMutation();
  const [uploadUrlCall] = useUploadUrlMutation();
  const [lazyDownloadUrlCall] = useLazyDownloadUrlQuery();
  const [submitKBApproval] = useSubmitKBApprovalMutation();
  const navigate = useNavigate();
  const { isKBApprovalEnabled, isKBApprovalDeletionEnabled } =
    useSelectedTeam();
  const isUserCriticalInTeam = useAppSelector(
    (state) => state.teamSettings.isUserCriticalInTeam,
  );
  const userAlias = useAppSelector((state) => state.auth.user.alias);

  const downloadSingleDoc = useCallback(
    async (doc: Document) => {
      try {
        const result = await lazyDownloadUrlCall({
          documentId: doc.documentId,
          preview: false,
        }).unwrap();
        if (result?.url) {
          window.location.href = result.url;
        }
      } catch {
        addNotification({
          type: "error",
          header: "Failed to download document.",
          dismissible: true,
          timeout: 10000,
        });
      }
    },
    [lazyDownloadUrlCall, addNotification],
  );

  const downloadDocs = useCallback(
    async (docs: Document[]) => {
      for (const doc of docs) {
        await downloadSingleDoc(doc);
        await sleep(500);
      }
    },
    [downloadSingleDoc],
  );

  // Atlas-specific upload function
  const uploadAtlasFiles = useCallback(
    async (
      files: File[],
      teamName: string,
      callback: () => void,
      replaceExistingMap: Map<string, boolean> = new Map(),
      existingDocs: Document[] = [],
      progressCallback?: (progress: UploadProgress) => void,
      cancelToken?: CancelToken,
    ) => {
      let successCount = 0;
      let failedCount = 0;

      try {
        const items: Array<{
          documentUrl: string;
          s3Key: string;
          fileName: string;
          extensionType: string;
          size: string;
          replaceExisting: boolean;
        }> = [];

        // Create a Map for O(1) lookup of existing documents by filename
        const existingDocsMap = new Map(
          existingDocs.map((doc) => [
            doc.fileName + (doc.extensionType || ""),
            doc,
          ]),
        );

        // Upload each file to S3 and prepare for Atlas ingestion
        for (let i = 0; i < files.length; i++) {
          // Check for cancellation before processing each file
          if (cancelToken?.cancelled) {
            break;
          }

          const file = files[i];

          try {
            // Update progress: starting new file
            if (progressCallback) {
              progressCallback({
                isUploading: true,
                currentFileIndex: i + 1,
                totalFiles: files.length,
                currentFileName: file.name,
                currentFileProgress: 0,
                successCount,
                failedCount,
                cancelRequested: cancelToken?.cancelled || false,
              });
            }

            // Check if this file is a duplicate and we're in replace mode
            const shouldReplace = replaceExistingMap.get(file.name) ?? false;
            const existingDoc = shouldReplace
              ? existingDocsMap.get(file.name) || null
              : null;

            // Extract uniqueKey from existing document if this is a replacement
            const existingUniqueKey = existingDoc
              ? extractUniqueKey(existingDoc.documentId)
              : null;

            // Call UploadPresignUrl with optional uniqueKey parameter
            const request: UploadUrlRequest & { uniqueKey?: string } = {
              prefix: "kb",
              fileName: file.name,
              teamName: teamName,
              securityLevel: "Normal",
              ...(existingUniqueKey && { uniqueKey: existingUniqueKey }),
            };

            const uploadUrlResponse = await uploadUrlCall(request).unwrap();

            if (!uploadUrlResponse) {
              throw new Error("Failed to get upload URL");
            }

            // Upload file to S3 with progress tracking
            await uploadFileWithProgress(
              uploadUrlResponse.url,
              file,
              uploadUrlResponse.bucketOwner,
              (percentage) => {
                if (progressCallback) {
                  progressCallback({
                    isUploading: true,
                    currentFileIndex: i + 1,
                    totalFiles: files.length,
                    currentFileName: file.name,
                    currentFileProgress: percentage,
                    successCount,
                    failedCount,
                    cancelRequested: cancelToken?.cancelled || false,
                  });
                }
              },
              cancelToken,
            );

            // Extract document URL from presigned URL
            const documentUrl = getDocumentUrlFromPresigned(
              uploadUrlResponse.url,
            );

            // Build s3Key in Atlas objectKey format: {teamName}/kb/{uniqueKey}/{fileName}
            const s3Key = `${teamName}/${buildS3Key(
              "kb",
              uploadUrlResponse.uniqueKey,
              file.name,
            )}`;

            // Extract file extension
            const extensionType = file.name.includes(".")
              ? file.name.substring(file.name.lastIndexOf(".") + 1)
              : "";

            // Add item with s3Key and replaceExisting flag
            items.push({
              documentUrl,
              s3Key,
              fileName: file.name,
              extensionType,
              size: getFileSize(file.size),
              replaceExisting: !!existingUniqueKey,
            });

            successCount++;
          } catch (fileError) {
            console.error(`Failed to upload ${file.name}:`, fileError);
            failedCount++;
            // Continue with next file
          }
        }

        // Call IngestDocuments API only if there are successful uploads
        // KB Approval flow handles its own notifications; track to skip standard ones
        let kbApprovalHandled = false;
        if (items.length > 0) {
          if (isKBApprovalEnabled) {
            kbApprovalHandled = true;
            if (isUserCriticalInTeam) {
              // Critical user: auto-approve via SubmitKBApproval API
              try {
                await submitKBApproval({
                  teamName,
                  documents: items.map((item) => ({
                    s3Key: item.s3Key,
                    fileName: getFileNameWithoutExtension(item.fileName),
                    extensionType: getFileExtension(item.fileName),
                    size: item.size,
                    replaceExisting: item.replaceExisting,
                  })),
                  approvers: [userAlias],
                  approvalMode: "OR",
                }).unwrap();

                addNotification({
                  type: "success",
                  header:
                    "Files uploaded and ingested into the knowledge base.",
                  dismissible: true,
                  timeout: 10000,
                });
              } catch {
                addNotification({
                  type: "error",
                  header: "Failed to submit KB approval.",
                  dismissible: true,
                  timeout: 10000,
                });
              }
            } else {
              // Non-critical user: navigate to approver selection
              navigate("/kb/approval/new", {
                state: {
                  files: items.map((item) => ({
                    s3Key: item.s3Key,
                    fileName: getFileNameWithoutExtension(item.fileName),
                    extensionType: getFileExtension(item.fileName),
                    size: item.size,
                    replaceExisting: item.replaceExisting,
                  })),
                  teamName,
                },
              });
              addNotification({
                type: "info",
                header:
                  "Files uploaded. Select approvers to submit for review.",
                dismissible: true,
                timeout: 10000,
              });
            }
          } else {
            // Standard flow: ingest directly without approval
            const request: IngestDocumentsRequest = {
              teamName: teamName,
              documentIngestions: {
                items: items.map((item) => ({
                  ...item,
                  teamName,
                })),
              },
            };

            await ingestDocuments(request).unwrap();
          }
        }

        callback();

        // Show standard upload notifications only when KB approval didn't handle them
        if (kbApprovalHandled) {
          // KB approval paths already showed their own notifications above
        } else if (cancelToken?.cancelled) {
          addNotification({
            type: "info",
            header: `Upload cancelled. ${successCount} of ${files.length} file${
              files.length > 1 ? "s" : ""
            } uploaded to Atlas${
              failedCount > 0 ? `, ${failedCount} failed` : ""
            }.`,
            dismissible: true,
            timeout: 10000,
          });
        } else if (failedCount === 0) {
          addNotification({
            type: "success",
            header: `${successCount} file${
              successCount > 1 ? "s" : ""
            } uploaded to Atlas successfully.`,
            dismissible: true,
            timeout: 10000,
          });
        } else if (successCount === 0) {
          addNotification({
            type: "error",
            header: `All ${files.length} file${
              files.length > 1 ? "s" : ""
            } failed to upload to Atlas.`,
            dismissible: true,
            timeout: 10000,
          });
        } else {
          addNotification({
            type: "warning",
            header: `${successCount} of ${files.length} file${
              files.length > 1 ? "s" : ""
            } uploaded to Atlas. ${failedCount} failed.`,
            dismissible: true,
            timeout: 10000,
          });
        }
      } catch (error) {
        callback();
        const is400Error =
          error &&
          typeof error === "object" &&
          "status" in error &&
          error.status === 400;
        addNotification({
          type: "error",
          header: is400Error
            ? "Atlas document upload failed - file type is not supported."
            : "Atlas document upload failed.",
          dismissible: true,
          timeout: 10000,
        });
      }
    },
    [
      uploadUrlCall,
      ingestDocuments,
      addNotification,
      isKBApprovalEnabled,
      isUserCriticalInTeam,
      userAlias,
      submitKBApproval,
      navigate,
    ],
  );

  const submitDeletionApproval = useCallback(
    async (docs: Document[]) => {
      const atlasObjectKeys = docs.map((doc) => doc.documentId);
      if (isUserCriticalInTeam) {
        try {
          await submitKBApproval({
            teamName,
            category: "Deletion",
            atlasObjectKeys,
            approvers: [userAlias],
            approvalMode: "OR",
          }).unwrap();
          addNotification({
            type: "success",
            header:
              atlasObjectKeys.length === 1
                ? "Document submitted for deletion."
                : "Documents submitted for deletion.",
            dismissible: true,
            timeout: 10000,
          });
        } catch {
          addNotification({
            type: "error",
            header:
              atlasObjectKeys.length === 1
                ? "Failed to submit document for deletion."
                : "Failed to submit documents for deletion.",
            dismissible: true,
            timeout: 10000,
          });
        }
      } else {
        const fileSizes: Record<string, string> = {};
        for (const doc of docs) {
          if (doc.size) fileSizes[doc.documentId] = doc.size;
        }
        addNotification({
          type: "info",
          header:
            atlasObjectKeys.length === 1
              ? "File selected for deletion. Select approvers to submit for review."
              : "Files selected for deletion. Select approvers to submit for review.",
          dismissible: true,
          timeout: 10000,
        });
        navigate("/kb/deletion-approval/new", {
          state: { atlasObjectKeys, teamName, fileSizes },
        });
      }
    },
    [
      isUserCriticalInTeam,
      submitKBApproval,
      teamName,
      userAlias,
      addNotification,
      navigate,
    ],
  );

  const deleteAtlasDoc = useCallback(
    async (doc: Document) => {
      if (isKBApprovalDeletionEnabled) {
        await submitDeletionApproval([doc]);
        return;
      }
      const atlasRequest = {
        teamName: teamName,
        documents: [doc.documentId],
      };
      const resp: DeleteIngestedDocumentsResponse =
        await deleteIngestedDocuments(atlasRequest).unwrap();
      if (resp.failedDocuments.length > 0) {
        resp.failedDocuments.forEach((d) => {
          console.error(
            `Failed to delete ${d.objectKey}, status=${d.deletionStatus} error=${d.errorMessage}`,
          );
        });
        throw new Error("Failed to delete ingested document");
      }
    },
    [
      deleteIngestedDocuments,
      teamName,
      isKBApprovalDeletionEnabled,
      submitDeletionApproval,
    ],
  );

  const deleteAtlasDocs = useCallback(
    async (docs: Document[]) => {
      if (isKBApprovalDeletionEnabled) {
        await submitDeletionApproval(docs);
        return;
      }
      // Delete Atlas documents in batches of up to 50
      for (let i = 0; i < docs.length; i += ATLAS_BATCH_SIZE) {
        const batch = docs.slice(i, i + ATLAS_BATCH_SIZE);
        const atlasRequest = {
          teamName: teamName,
          documents: batch.map((doc) => doc.documentId),
        };
        const resp: DeleteIngestedDocumentsResponse =
          await deleteIngestedDocuments(atlasRequest).unwrap();

        if (resp.failedDocuments.length > 0) {
          resp.failedDocuments.forEach((d) => {
            console.error(
              `Failed to delete ${d.objectKey}, status=${d.deletionStatus} error=${d.errorMessage}`,
            );
          });
          throw new Error("Failed to delete some ingested documents");
        }
      }
    },
    [
      deleteIngestedDocuments,
      teamName,
      isKBApprovalDeletionEnabled,
      submitDeletionApproval,
    ],
  );

  return {
    uploadAtlasFiles,
    deleteAtlasDoc,
    deleteAtlasDocs,
    downloadSingleDoc,
    downloadDocs,
  };
};
